import type {BeefreeDocument} from 'src/components/lib/beefree-editor';
import type {Brand} from 'src/types/brands';
import {DEFAULT_BEEFREE_LOGO_URL} from 'src/utils/automation-workflow';
import {SIMPLE_TEMPLATE_START_TEXT} from 'src/utils/email';


export default function templateFactory({
  includeChatbotPlaceholder = false,
  brandingSettings,
  isSimpleTemplate = false,
}: {
  includeChatbotPlaceholder: boolean;
  brandingSettings: Brand | null | undefined;
  isSimpleTemplate: boolean;
}): BeefreeDocument {
  const agencyColor = brandingSettings?.color || '#0068A5';
  const agencyLogoUrl = brandingSettings?.logo || DEFAULT_BEEFREE_LOGO_URL;

  const footerColumn = {
    columns: [
      {
        'grid-columns': 12,
        modules: [
          {
            descriptor: {
              computedStyle: {
                hideContentOnMobile: false,
              },
              style: {
                'padding-bottom': '5px',
                'padding-left': '5px',
                'padding-right': '5px',
                'padding-top': '5px',
              },
              text: {
                computedStyle: {
                  linkColor: '#0068A5',
                },
                html: '<div class="txtTinyMce-wrapper" style="font-family: Arial, &quot;Helvetica Neue&quot;, Helvetica, sans-serif; font-size: 12px; line-height: 18px; text-align:center;" data-mce-style="font-family: Arial, &quot;Helvetica Neue&quot;, Helvetica, sans-serif; font-size: 12px; line-height: 18px; text-align:center;"><span style="font-size:12px;line-height:14px;" data-mce-style="font-size:12px;line-height:14px;">{{reason_for_receiving_email}} {{unsubscribe_link}} | {{manage_subscription_link}}</span></div>',
                style: {
                  color: '#787878',
                  'font-family': 'inherit',
                  'line-height': '150%',
                },
              },
            },
            locked: true,
            type: 'mailup-bee-newsletter-modules-text',
          },
        ],
        style: {
          'background-color': 'transparent',
          'border-bottom': '0px solid transparent',
          'border-left': '0px solid transparent',
          'border-right': '0px solid transparent',
          'border-top': '0px solid transparent',
          'padding-bottom': '0px',
          'padding-left': '0px',
          'padding-right': '0px',
          'padding-top': '0px',
        },
      },
    ],
    container: {
      style: {
        'background-color': 'transparent',
        'background-image': 'none',
        'background-position': 'top left',
        'background-repeat': 'no-repeat',
      },
    },
    content: {
      computedStyle: {
        hideContentOnDesktop: false,
        hideContentOnMobile: false,
        rowColStackOnMobile: true,
        rowReverseColStackOnMobile: false,
        verticalAlign: 'top',
      },
      style: {
        'background-color': 'transparent',
        'background-image': 'none',
        'background-position': 'top left',
        'background-repeat': 'no-repeat',
        color: '#000000',
        width: '600px',
      },
    },
    empty: false,
    footer: true,
    locked: false,
    synced: false,
    type: 'one-column-empty',
  };

  const standardTemplateRows = [
    {
      columns: [
        {
          'grid-columns': 12,
          modules: [
            {
              descriptor: {
                computedStyle: {
                  class: 'center fixedwidth',
                  hideContentOnMobile: false,
                  width: 120,
                },
                image: {
                  alt: '',
                  href: '',
                  src: `${agencyLogoUrl}`,
                },
                style: {
                  'padding-bottom': '0px',
                  'padding-left': '0px',
                  'padding-right': '0px',
                  'padding-top': '0px',
                  width: '100%',
                },
              },
              locked: false,
              type: 'mailup-bee-newsletter-modules-image',
            },
          ],
          style: {
            'background-color': 'transparent',
            'border-bottom': '0px solid transparent',
            'border-left': '0px solid transparent',
            'border-right': '0px solid transparent',
            'border-top': '0px solid transparent',
            'padding-bottom': '25px',
            'padding-left': '0px',
            'padding-right': '0px',
            'padding-top': '25px',
          },
        },
      ],
      container: {
        style: {
          'background-color': 'transparent',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
        },
      },
      content: {
        computedStyle: {
          hideContentOnDesktop: false,
          hideContentOnMobile: false,
          rowColStackOnMobile: true,
          rowReverseColStackOnMobile: false,
          verticalAlign: 'top',
        },
        style: {
          'background-color': 'transparent',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
          'border-bottom': '0px solid transparent',
          'border-left': '0px solid transparent',
          'border-radius': '0px',
          'border-right': '0px solid transparent',
          'border-top': '0px solid transparent',
          color: '#000000',
          width: '600px',
        },
      },
      empty: false,
      locked: false,
      synced: false,
      type: 'one-column-empty',
    },
    {
      columns: [
        {
          'grid-columns': 12,
          modules: [
            {
              descriptor: {
                computedStyle: {
                  hideContentOnMobile: false,
                },
                style: {
                  'padding-bottom': '10px',
                  'padding-left': '10px',
                  'padding-right': '10px',
                  'padding-top': '10px',
                },
                text: {
                  computedStyle: {
                    linkColor: `${agencyColor}`,
                  },
                  html: '<div class="txtTinyMce-wrapper" style="font-family:inherit;font-size:12px;line-height:14px;" data-mce-style="font-family:inherit;font-size:12px;line-height:14px;"><p style="font-size:14px;line-height:16px;word-break:break-word;text-align:left;" data-mce-style="font-size:14px;line-height:16px;word-break:break-word;text-align:left;">Hi, you can include any variable in this email using the dynamic label. If you want to add more elements, such as a CTA text, a text box, or an image, you can simply drag them here. We have already provided a header and footer that you can customize.</p></div>',
                  style: {
                    color: '#5d5959',
                    'font-family': 'inherit',
                    'line-height': '120%',
                  },
                },
              },
              locked: false,
              type: 'mailup-bee-newsletter-modules-text',
            },
          ],
          style: {
            'background-color': 'transparent',
            'border-bottom': '0px solid transparent',
            'border-left': '0px solid transparent',
            'border-right': '0px solid transparent',
            'border-top': '0px solid transparent',
            'padding-bottom': '0px',
            'padding-left': '0px',
            'padding-right': '0px',
            'padding-top': '0px',
          },
        },
      ],
      container: {
        style: {
          'background-color': 'transparent',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
        },
      },
      content: {
        computedStyle: {
          hideContentOnDesktop: false,
          hideContentOnMobile: false,
          rowColStackOnMobile: true,
          rowReverseColStackOnMobile: false,
          verticalAlign: 'top',
        },
        style: {
          'background-color': '#FFFFFF',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
          color: '#000000',
          width: '600px',
        },
      },
      empty: false,
      locked: false,
      synced: false,
      type: 'one-column-empty',
    },
    {
      columns: [
        {
          'grid-columns': 12,
          modules: [
            {
              descriptor: {
                computedStyle: {
                  height: 57,
                  hideContentOnMobile: false,
                  iconsDefaultHeight: 32,
                  iconsDefaultWidth: 32,
                  padding: '0 5px 5px 0',
                  width: 188,
                },
                iconsList: {
                  icons: [
                    {
                      image: {
                        alt: 'Facebook',
                        href: 'https://www.facebook.com/',
                        prefix: 'https://www.facebook.com/',
                        src: 'https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-dark-gray/facebook@2x.png',
                        target: '_self',
                        title: 'Facebook',
                      },
                      name: 'facebook',
                      text: '',
                      type: 'follow',
                    },
                    {
                      image: {
                        alt: 'Twitter',
                        href: 'https://twitter.com/',
                        prefix: 'https://twitter.com/',
                        src: 'https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-dark-gray/twitter@2x.png',
                        target: '_self',
                        title: 'Twitter',
                      },
                      name: 'twitter',
                      text: '',
                      type: 'follow',
                    },
                    {
                      image: {
                        alt: 'Instagram',
                        href: 'https://instagram.com/',
                        prefix: 'https://instagram.com/',
                        src: 'https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-dark-gray/instagram@2x.png',
                        target: '_self',
                        title: 'Instagram',
                      },
                      name: 'instagram',
                      text: '',
                      type: 'follow',
                    },
                    {
                      image: {
                        alt: 'LinkedIn',
                        href: 'https://www.linkedin.com/',
                        prefix: 'https://www.linkedin.com/',
                        src: 'https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-dark-gray/linkedin@2x.png',
                        target: '_self',
                        title: 'LinkedIn',
                      },
                      name: 'linkedin',
                      text: '',
                      type: 'follow',
                    },
                  ],
                },
                style: {
                  'padding-bottom': '10px',
                  'padding-left': '10px',
                  'padding-right': '10px',
                  'padding-top': '10px',
                  'text-align': 'center',
                },
              },
              locked: false,
              type: 'mailup-bee-newsletter-modules-social',
            },
            {
              descriptor: {
                computedStyle: {
                  hideContentOnMobile: false,
                },
                style: {
                  'padding-bottom': '10px',
                  'padding-left': '10px',
                  'padding-right': '10px',
                  'padding-top': '10px',
                },
                text: {
                  computedStyle: {
                    linkColor: `${agencyColor}`,
                  },
                  html: '<div class="txtTinyMce-wrapper" style="font-size:14px;line-height:16px;font-family:inherit;" data-mce-style="font-size:14px;line-height:16px;font-family:inherit;"><p style="font-size:14px;line-height:16px;word-break:break-word;text-align:center;" data-mce-style="font-size:14px;line-height:16px;word-break:break-word;text-align:center;">companywebsite.com</p></div>',
                  style: {
                    color: '#555555',
                    'font-family': 'inherit',
                    'line-height': '120%',
                  },
                },
              },
              locked: false,
              type: 'mailup-bee-newsletter-modules-text',
            },
            {
              descriptor: {
                computedStyle: {
                  hideContentOnMobile: false,
                },
                style: {
                  'padding-bottom': '10px',
                  'padding-left': '10px',
                  'padding-right': '10px',
                  'padding-top': '10px',
                },
                text: {
                  computedStyle: {
                    linkColor: `${agencyColor}`,
                  },
                  html: '<div class="txtTinyMce-wrapper" style="font-size:14px;line-height:16px;font-family:inherit;" data-mce-style="font-size:14px;line-height:16px;font-family:inherit;"><p style="font-size:14px;line-height:16px;word-break:break-word;text-align:center;" data-mce-style="font-size:14px;line-height:16px;word-break:break-word;text-align:center;"><span style="font-size:12px;line-height:14px;" data-mce-style="font-size:12px;line-height:14px;">Company address line 1, street, city, state, 112233</span></p><p style="font-size:14px;line-height:16px;word-break:break-word;text-align:center;" data-mce-style="font-size:14px;line-height:16px;word-break:break-word;text-align:center;"><span style="font-size:12px;line-height:14px;" data-mce-style="font-size:12px;line-height:14px;">+1 2332123321</span></p></div>',
                  style: {
                    color: '#555555',
                    'font-family': 'inherit',
                    'line-height': '120%',
                  },
                },
              },
              locked: false,
              type: 'mailup-bee-newsletter-modules-text',
            },
          ],
          style: {
            'background-color': 'transparent',
            'border-bottom': '0px solid transparent',
            'border-left': '0px solid transparent',
            'border-right': '0px solid transparent',
            'border-top': '0px solid transparent',
            'padding-bottom': '0px',
            'padding-left': '0px',
            'padding-right': '0px',
            'padding-top': '0px',
          },
        },
      ],
      container: {
        style: {
          'background-color': '#e8e8e8',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
        },
      },
      content: {
        computedStyle: {
          hideContentOnDesktop: false,
          hideContentOnMobile: false,
          rowColStackOnMobile: true,
          rowReverseColStackOnMobile: false,
          verticalAlign: 'top',
        },
        style: {
          'background-color': 'transparent',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
          color: '#000000',
          width: '600px',
        },
      },
      empty: false,
      locked: false,
      synced: false,
      type: 'one-column-empty',
    },
    footerColumn,
  ];

  const simpleTemplateRows = [
    {
      columns: [
        {
          'grid-columns': 12,
          modules: [
            {
              descriptor: {
                computedStyle: {
                  hideContentOnMobile: false,
                },
                style: {
                  'padding-bottom': '10px',
                  'padding-left': '10px',
                  'padding-right': '10px',
                  'padding-top': '10px',
                },
                text: {
                  computedStyle: {
                    linkColor: `${agencyColor}`,
                  },
                  html: `<div class="txtTinyMce-wrapper" style="font-family:inherit;font-size:12px;line-height:14px;" 
                  data-mce-style="font-family:inherit;font-size:12px;line-height:14px;">
                  <p style="font-size:14px;line-height:16px;word-break:break-word;text-align:left;" 
                  data-mce-style="font-size:14px;line-height:16px;word-break:break-word;text-align:left;">
                  ${SIMPLE_TEMPLATE_START_TEXT}</p></div>`,
                  style: {
                    color: '#5d5959',
                    'font-family': 'inherit',
                    'line-height': '120%',
                  },
                },
              },
              locked: false,
              type: 'mailup-bee-newsletter-modules-text',
            },
          ],
          style: {
            'background-color': 'transparent',
            'border-bottom': '0px solid transparent',
            'border-left': '0px solid transparent',
            'border-right': '0px solid transparent',
            'border-top': '0px solid transparent',
            'padding-bottom': '0px',
            'padding-left': '0px',
            'padding-right': '0px',
            'padding-top': '0px',
          },
        },
      ],
      container: {
        style: {
          'background-color': 'transparent',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
        },
      },
      content: {
        computedStyle: {
          hideContentOnDesktop: false,
          hideContentOnMobile: false,
          rowColStackOnMobile: true,
          rowReverseColStackOnMobile: false,
          verticalAlign: 'top',
        },
        style: {
          'background-color': 'transparent',
          'background-image': 'none',
          'background-position': 'top left',
          'background-repeat': 'no-repeat',
          color: '#000000',
          width: '600px',
        },
      },
      empty: false,
      footer: false,
      locked: false,
      synced: false,
      type: 'one-column-empty',
    },
    footerColumn,
  ];

  const rows = isSimpleTemplate ? simpleTemplateRows : standardTemplateRows;

  //include a chatbot placeholder just before last row in rows
  if (includeChatbotPlaceholder) {
    if (isSimpleTemplate) {
      // @ts-expect-error - TS2345 - Argument of type '{ content: { computedStyle: { rowColStackOnMobile: boolean; rowReverseColStackOnMobile: boolean; verticalAlign: string; hideContentOnMobile: boolean; hideContentOnDesktop: boolean; }; }; type: string; empty: boolean; rowInternal: { ...; }; columns: { ...; }[]; }' is not assignable to parameter of type '{ columns: { 'grid-columns': number; modules: { descriptor: { computedStyle: { class: string; hideContentOnMobile: boolean; width: number; }; image: { alt: string; href: string; src: string; }; style: { 'padding-bottom': string; 'padding-left': string; 'padding-right': string; 'padding-top': string; width: string; }...'.
      rows.splice(rows.length - 1, 0, getChatbotPlaceholderRow());
    } else {
      // @ts-expect-error - TS2345 - Argument of type '{ content: { computedStyle: { rowColStackOnMobile: boolean; rowReverseColStackOnMobile: boolean; verticalAlign: string; hideContentOnMobile: boolean; hideContentOnDesktop: boolean; }; }; type: string; empty: boolean; rowInternal: { ...; }; columns: { ...; }[]; }' is not assignable to parameter of type '{ columns: { 'grid-columns': number; modules: { descriptor: { computedStyle: { class: string; hideContentOnMobile: boolean; width: number; }; image: { alt: string; href: string; src: string; }; style: { 'padding-bottom': string; 'padding-left': string; 'padding-right': string; 'padding-top': string; width: string; }...'.
      rows.splice(rows.length - 2, 0, getChatbotPlaceholderRow());
    }
  }

  return {
    page: {
      body: {
        container: {
          style: {
            'background-color': '#ffffff',
          },
        },
        content: {
          computedStyle: {
            linkColor: `${agencyColor}`,
            messageBackgroundColor: 'transparent',
            messageWidth: '600px',
          },
          style: {
            color: '#000000',
            'font-family': 'Arial, \'Helvetica Neue\', Helvetica, sans-serif',
          },
        },
        type: 'mailup-bee-page-properties',
        webFonts: [],
      },
      description: '',
      rows,
      template: {
        name: 'template-base',
        type: 'basic',
        version: '2.0.0',
      },
      title: '',
    },
  };
}

/** Exported for use in applyBrandingToBeefreeDocument (utils). */
export const getChatbotPlaceholderRow = () => {
  return {
    content: {
      computedStyle: {
        rowColStackOnMobile: true,
        rowReverseColStackOnMobile: false,
        verticalAlign: 'top',
        hideContentOnMobile: false,
        hideContentOnDesktop: false,
      },
    },
    type: 'rowAddon',
    empty: true,
    rowInternal: {
      uid: 'sense-chatbot-row',
      entity: 'chatbot',
      icon: 'data:image/svg+xml;base64,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',
      placeholder: 'Click here to configure the chatbot addon',
      ctaLabel: 'Configure',
      configurationUi: {
        contentDialogId: 'sense-chatbot-row',
        external: null,
      },
    },
    columns: [
      {
        'grid-columns': 12,
        modules: [],
        style: {
          'background-color': 'transparent',
          'border-bottom': '0px solid transparent',
          'border-left': '0px solid transparent',
          'border-right': '0px solid transparent',
          'border-top': '0px solid transparent',
          'padding-bottom': '5px',
          'padding-left': '0px',
          'padding-right': '0px',
          'padding-top': '5px',
        },
      },
    ],
  };
};
